import { ComponentProps, useMemo } from 'react'

import { genericMemo } from 'utils'
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form'

import { useTranslation } from '@crew/modules/i18n'

import { CrewErrorMessages } from 'components/forms/crewErrorMessages'
import { CrewColorSelectBox } from 'components/devextreme/crewColorSelectBox'
import { CrewFieldLabel } from 'components/elements/crewFieldLabel'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'

type Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = Omit<
  ComponentProps<typeof CrewColorSelectBox>,
  'ref' | 'name' | 'value' | 'onValueChange' | 'onFocusOut' | 'isValid' //これらはcontrollerが提供するのでpropsから除外する
> & {
  id: string
  required?: boolean
  label?: string
  showLabel?: boolean
  previewText?: string
} & UseControllerProps<TFieldValues, TName>

/**
 * バッジ色を選択する専用のreact-hook-form対応のSelectBox
 */
export const CrewBadgeColorSelectBoxField = genericMemo(
  <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
    // useController用
    name,
    rules,
    control,
    shouldUnregister,
    defaultValue,
    // ラベル用
    id,
    required = false,
    label,
    showLabel = true,
    // プレビュー用
    previewText,
    // その他
    children,
    ...rest
  }: Props<TFieldValues, TName>) => {
    const { field, fieldState, formState } = useController({
      name,
      control,
      rules,
      shouldUnregister,
      defaultValue,
    })

    const { t } = useTranslation()

    /**
     * バッジプレビューで表示するテキスト
     */
    const badgeText = useMemo(
      () => previewText || t('label.notSet'), // ?? ではなく || を使い、空文字の場合も「未設定」を表示させる
      [previewText, t]
    )

    return (
      <div className="flex flex-col gap-1">
        {showLabel && (
          <CrewFieldLabel text={label} required={required} htmlFor={id} />
        )}
        <div className="flex items-center gap-4">
          <CrewColorSelectBox
            {...rest}
            id={id}
            data-testid={field.name}
            name={field.name}
            value={field.value}
            onValueChange={field.onChange}
            onFocusOut={field.onBlur}
            isValid={!fieldState.error}
          >
            {children}
          </CrewColorSelectBox>

          {/* バッジプレビュー */}
          <CrewBadge displayColor={field.value}>{badgeText}</CrewBadge>
        </div>
        <CrewErrorMessages
          isValid={!fieldState.error}
          errors={formState.errors}
          field={field}
        />
      </div>
    )
  }
)
